import moment from 'moment'

const debug = true
const reportTime = time => moment(time).utc().format()
const logPrefix = (action, duration, assetId, timeFrom, timeTo) =>
  'LogsCache.' + action + ' @ ' + duration + ' ms, asset id: ' + assetId +
  ', interval: ' + reportTime(timeFrom) + ' - ' + reportTime(timeTo)

const findIndex = (values, time) => {
  const lastValuesTime = values[values.length - 1][0]
  if (lastValuesTime < time) {
    return values.length
  }
  for (let i = 0; i < values.length; i++) { // TODO optimize by halving the intervals?
    const valuesTime = values[i][0]
    if (valuesTime === time) {
      return null // time already present, no change of cached data required
    }
    if (time < valuesTime) {
      return i
    }
  }
  return values.length // should never get here
}

const mergeVariableValues = (originalValues, savingValues) => {
  if (originalValues.length === 0) { // saving into empty existing values => append
    originalValues.push(...savingValues)
  } else if (savingValues.length === 0) { // saving empty new values => no-op
  } else if (originalValues[originalValues.length - 1][0] < savingValues[0][0]) { // all new after original => append
    originalValues.push(...savingValues)
  } else if (savingValues[savingValues.length - 1][0] < originalValues[0][0]) { // all new before original => insert at start
    originalValues.splice(0, 0, ...savingValues)
  } else { // overlap => merge one by one
    savingValues.forEach(([time, value]) => {
      const insertIndex = findIndex(originalValues, time)
      if (insertIndex !== null) {
        originalValues.splice(insertIndex, 0, [time, value])
      }
    })
  }
}

const mergeData = (originalData, savingData) => {
  Object.keys(savingData.variables)
    .forEach(variableName => {
      const originalVariable = originalData.variables[variableName]
      const originalValues = originalVariable ? originalVariable.values : []
      const savingVariable = savingData.variables[variableName]
      const savingValues = savingVariable ? savingVariable.values : []
      mergeVariableValues(originalValues, savingValues)
    })
}

const subtractExistingInterval = (missingIntervals, existingInterval) => {
  const result = []
  missingIntervals.forEach(missingInterval => {
    if (missingInterval.timeTo <= existingInterval.timeFrom ||
      existingInterval.timeTo <= missingInterval.timeFrom) { // no overlap => keep missing as-is
      result.push(missingInterval)
      return
    }
    // overlap
    const startCovered = missingInterval.timeFrom >= existingInterval.timeFrom
    const endCovered = missingInterval.timeTo <= existingInterval.timeTo
    if (!startCovered) { // start of missing not covered => keep it
      result.push({ timeFrom: missingInterval.timeFrom, timeTo: existingInterval.timeFrom })
    }
    if (!endCovered) { // end of missing not covered => keep it
      result.push({ timeFrom: existingInterval.timeTo, timeTo: missingInterval.timeTo })
    }
  })
  return result
}

export default class LogsCache {
  constructor () {
    this.cache = {}
  }

  calculateMissingIntervals ({ assetId, timeFrom, timeTo }) {
    const startTime = new Date()
    let missingIntervals = [{ timeFrom, timeTo }]
    const assetCache = this.cache[assetId]
    assetCache && assetCache.coverage.forEach(existingInterval => {
      missingIntervals = subtractExistingInterval(missingIntervals, existingInterval)
    })
    debug && console.log(logPrefix('missing', new Date() - startTime, assetId, timeFrom, timeTo),
      '=> missing intervals', missingIntervals)
    return missingIntervals
  }

  getData ({ assetId, timeFrom, timeTo }) {
    const startTime = new Date()
    const assetCache = this.cache[assetId]
    if (assetCache) {
      const result = { ...assetCache.data }
      result.variables = {}
      Object.entries(assetCache.data.variables)
        .forEach(([variableName, variable]) => {
          const values = []
          for (let i = 0; i < variable.values.length; i++) {
            const tuple = variable.values[i]
            const lastTuple = i === variable.values.length - 1
            const nextTime = !lastTuple && variable.values[i + 1][0]
            const time = tuple[0]
            // Include data when either it falls into the interval,
            // or it is the last one before the interval,
            // or it is the last value and no value has been included yet
            //   as all values have been before the requested interval.
            // This ensures that the line is drawn from the start (left side) of the window
            if (((nextTime || time) >= timeFrom && time <= timeTo) ||
              (lastTuple && time < timeFrom)) {
              values.push(tuple)
            }
          }
          result.variables[variableName] = { ...variable, values }
        })
      debug && console.log(logPrefix('get', new Date() - startTime, assetId, timeFrom, timeTo), '=> data', result)
      return result
    }
    debug && console.log(logPrefix('get', new Date() - startTime, assetId, timeFrom, timeTo), '=> no data')
  }

  saveData ({ assetId, timeFrom, timeTo, now, data }) {
    const startTime = new Date()
    const assetCache = this.cache[assetId]
    const adjustedTimeFrom = now && now < timeFrom ? now : timeFrom
    const adjustedTimeTo = now && now < timeTo ? now : timeTo
    if (!assetCache) {
      this.cache[assetId] = { coverage: [{ timeFrom: adjustedTimeFrom, timeTo: adjustedTimeTo }], data }
    } else {
      assetCache.coverage.push({ timeFrom: adjustedTimeFrom, timeTo: adjustedTimeTo }) // TODO merge intervals
      mergeData(assetCache.data, data)
    }
    debug && console.log(logPrefix('save', new Date() - startTime, assetId, timeFrom, timeTo),
      '=> saving', data, 'saved', this.cache[assetId],
      'values #', this.cache[assetId].data.variables)
  }

  clear (assetId) {
    if (assetId === undefined) {
      this.cache = {}
    } else {
      delete this.cache[assetId]
    }
  }
}
